import { AnyAction, ThunkAction } from '@reduxjs/toolkit';
import type rootReducer from 'redux/reducers/rootReducer';
import { isStudyNotFound } from 'helper/flux/myStudyErrors';
import { sendInterfaceErrorToSentry } from 'helper/sendErrorToSentry';
import {
  MyStudiesListResult,
  toMyStudyScenariosResult,
} from 'helper/flux/myStudiesAdapter';
import {
  MyStudiesQuery,
  MyStudyScenario,
  RawMyStudyScenario,
} from 'types/Flux/Personalized/myStudies';
import baseApi, { GenericResponse } from './base';
import {
  companiesEndpoint,
  creatorsEndpoint,
  listStudiesEndpoint,
  MyStudiesEndpointsConfig,
  reportRejected,
  studiesStatusEndpoint,
  studiesUrl,
} from './myStudiesEndpoints';

const ROOT = 'produtos/previvaz-personalizado/estudos';
const CONFIG: MyStudiesEndpointsConfig = { root: ROOT, listTag: 'PersonalizedStudy' };

/**
 * Cenários mudam pouco: ficam 30 min em cache. A tag `PersonalizedStudyScenarios` é fornecida
 * por id, e nenhuma mutation a invalida: excluir tira a linha da tela, e cancelar ou reenviar
 * não mudam os cenários. A atualização ao expandir a linha é da #2002.
 */
const SCENARIOS_KEEP_UNUSED_SECONDS = 1800;

const SCENARIOS_NOT_FOUND_TITLE = 'MEUS ESTUDOS: cenários de estudo inexistente (404)';

/**
 * 404 dos cenários (#2002), uma vez por requisição, com ou sem painel aberto: o estudo saiu
 * (outra pessoa o excluiu), então a lista é invalidada e a linha some quando a lista nova chega.
 * O backend responde o mesmo `code: 404` a uma rota desconhecida, então o 404 também vai ao Sentry,
 * como aviso e numa issue própria: uma rota quebrada (deploy parcial) aparece lá, e o estudo
 * excluído não vira erro.
 */
function handleScenariosNotFound(
  studyId: number,
  error: unknown,
  dispatch: (action: ReturnType<typeof baseApi.util.invalidateTags>) => unknown,
): void {
  if (!isStudyNotFound(error)) return;
  dispatch(baseApi.util.invalidateTags(['PersonalizedStudy']));
  const { data: message } = error as { data: string };
  sendInterfaceErrorToSentry(
    SCENARIOS_NOT_FOUND_TITLE,
    `getMyStudyScenarios: estudo ${studyId} respondeu 404 ("${message}")`,
    new Error(message),
    {
      level: 'warning',
      tags: { endpoint: 'getMyStudyScenarios' },
      fingerprint: ['meus-estudos', 'cenarios', '404'],
    },
  );
}

/**
 * Slice das 5 rotas GET da aba Meus Estudos (#1993). Os nomes de wire e as datas ficam no
 * `helper/flux/myStudiesAdapter`; aqui só se desembrulha o envelope e se aplica o adapter.
 */
export const myStudiesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listMyStudies: listStudiesEndpoint(build, CONFIG, 'listMyStudies'),
    getMyStudyScenarios: build.query<MyStudyScenario[], number>({
      query: (studyId) => ({
        url: studiesUrl(ROOT, `${studyId}/cenarios/`),
        method: 'GET',
      }),
      transformResponse: (response: GenericResponse<RawMyStudyScenario[]>) => {
        const { scenarios, rejected } = toMyStudyScenariosResult(response.data);
        reportRejected('getMyStudyScenarios', rejected);
        return scenarios;
      },
      providesTags: (_result, _error, studyId) => [{ type: 'PersonalizedStudyScenarios', id: studyId }],
      keepUnusedDataFor: SCENARIOS_KEEP_UNUSED_SECONDS,
      async onQueryStarted(studyId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (rejection) {
          handleScenariosNotFound(studyId, (rejection as { error?: unknown }).error, dispatch);
        }
      },
    }),
    getMyStudiesStatus: studiesStatusEndpoint(build, CONFIG, 'getMyStudiesStatus'),
    listMyStudyCompanies: companiesEndpoint(build, CONFIG, 'listMyStudyCompanies'),
    listMyStudyCreators: creatorsEndpoint(build, CONFIG, 'listMyStudyCreators'),
  }),
});

export const {
  useListMyStudiesQuery,
  useGetMyStudyScenariosQuery,
  useGetMyStudiesStatusQuery,
  useListMyStudyCompaniesQuery,
  useListMyStudyCreatorsQuery,
} = myStudiesApi;

/**
 * O que a aba Meus Estudos precisa da API de um produto (Personalizado ou Upload). Os hooks
 * acoplados (`useMyStudiesPage`, `useMyStudiesStatusPolling`, filtros) leem este objeto do
 * `MyStudiesProductContext` em vez de importar o slice; sem Provider, vale o do Personalizado.
 */
export interface MyStudiesApiPort {
  useListQuery: typeof useListMyStudiesQuery;
  useCompaniesQuery: typeof useListMyStudyCompaniesQuery;
  useCreatorsQuery: typeof useListMyStudyCreatorsQuery;
  useListQueryState: typeof myStudiesApi.endpoints.listMyStudies.useQueryState;
  initiateStatus: typeof myStudiesApi.endpoints.getMyStudiesStatus.initiate;
  /** `updateQueryData` da lista deste produto, com o nome do endpoint já resolvido. */
  updateList: (
    args: MyStudiesQuery,
    recipe: (draft: MyStudiesListResult) => void,
  ) => ThunkAction<
  ReturnType<ReturnType<typeof myStudiesApi.util.updateQueryData>>,
  ReturnType<typeof rootReducer>, undefined, AnyAction>;
  /** Invalida a tag da lista deste produto. */
  invalidateList: () => ReturnType<typeof baseApi.util.invalidateTags>;
}

export const myStudiesApiPort: MyStudiesApiPort = {
  useListQuery: useListMyStudiesQuery,
  useCompaniesQuery: useListMyStudyCompaniesQuery,
  useCreatorsQuery: useListMyStudyCreatorsQuery,
  useListQueryState: myStudiesApi.endpoints.listMyStudies.useQueryState,
  initiateStatus: myStudiesApi.endpoints.getMyStudiesStatus.initiate,
  updateList: (args, recipe) => myStudiesApi.util.updateQueryData('listMyStudies', args, recipe),
  invalidateList: () => myStudiesApi.util.invalidateTags(['PersonalizedStudy']),
};
